import jstz from "jstz";
import { createContext, ReactNode, useContext, useEffect, useReducer } from "react";
import { UILanguage } from "../../common/enums/UILanguage";
import { IeLynxUser } from "../../common/models/eLynxUser";

import { ICustomer } from "../../common/models/ICustomer";
import { IUIRefreshInterval } from "../../services/state-data-service/Models";
import storageService from "../../services/storage/storage-service/StorageService";
import { StorageKeys } from "../../services/storage/StorageKeys";
import { IUserSettings } from "../../services/user-settings-data/Models";


export type UserState = {
    authenticated: boolean;
    customer?: ICustomer | undefined;
    user?: IeLynxUser | undefined;

    contextOrganizationID?: number | undefined;
    startGroupID?: number | undefined;
    language: UILanguage;
    unitSetID: number;
    timezone: string;
    refreshIntervals: IUIRefreshInterval[];

    defaultURL?: string;
    userSettings?: IUserSettings;
};

export const defaultUserState: UserState = {
    authenticated: false,
    timezone: jstz.determine().name(),
    unitSetID: 0,
    language: UILanguage.USEnglish,
    refreshIntervals: []
};

export enum UserStateActionTypeEnum {
    Authenticated = "userState.authenticated",
    UserState = "userState",
    ResetUserState = "resetUserState",
    DefaultUrlChanged = "DEFAULT_URL_CHANGED",
    UserChanged = 'userState.UserChanged'    
}

export type UserStateAction = {
    type: UserStateActionTypeEnum.Authenticated,
    authenticated: boolean
} | {
    type: UserStateActionTypeEnum.UserState,
    userState: UserState
} | {
    type: UserStateActionTypeEnum.ResetUserState
} | {
    type: UserStateActionTypeEnum.DefaultUrlChanged,
    defaultUrl: string
} | {
    type: UserStateActionTypeEnum.UserChanged,
    user: IeLynxUser
}


const reducer = (state: UserState, action: UserStateAction): UserState => {
    switch (action.type) {
        case UserStateActionTypeEnum.Authenticated:
            //const customer = action.authenticated ? state.customer : undefined;
            return {
                ...state,
                //customer: customer,
                authenticated: action.authenticated
            };

        case UserStateActionTypeEnum.UserState: {

            const newUserState: UserState =  {
                ...action.userState,
                timezone: state.timezone
            };

            const key = getUserStateKey();
            storageService.local.set(key, newUserState);
            storageService.session.set(key, newUserState);
            return newUserState;
        }

        case UserStateActionTypeEnum.DefaultUrlChanged:
            return {
                ...state,
                defaultURL: action.defaultUrl
            };

        case UserStateActionTypeEnum.ResetUserState:

            //storageService.local.remove(StorageKeys.LastCustomerID);
            //storageService.local.remove(StorageKeys.IsSSO);
            const key = getUserStateKey();
            storageService.local.remove(key);
            storageService.session.remove(key);

            return {
                ...defaultUserState
            };

        case UserStateActionTypeEnum.UserChanged:
            return {
                ...state,
                user: action.user,
                userSettings: {
                    ...state.userSettings,
                    mfaSource: action.user.mfaSource,       // Need to update this so that the MFA source will be displayed correctly upon subsequent visits to the Account Settings page
                    mfaSecret: action.user.mfaSecret ?? ""  // Need to update this so that the QR code will be displayed correctly upon subsequent visits to the Account Settings page
                } as IUserSettings
            };        

        default:
            return state
    }
};

export type UserContextProviderProps = { 
    children: ReactNode, 
};

const UserContext = createContext<[UserState, (action: UserStateAction) => void] | undefined>(undefined);

export const getUserStateKey = (local?: boolean) => local ? 
`${storageService.local.get(StorageKeys.LastCustomerID, 0)}:${storageService.local.get(StorageKeys.LastUserName, '').toLowerCase()}:user-state` :
`${storageService.session.get(StorageKeys.LastCustomerID, 0)}:${storageService.session.get(StorageKeys.LastUserName, '').toLowerCase()}:user-state`;

function storeState(userState: UserState): void {
    //if (userState.customer && userState.user) {
        const lastCustomerID = storageService.session.get(StorageKeys.LastCustomerID, 0);
        const lastUserName = storageService.session.get(StorageKeys.LastUserName, '');
        //console.log(`userState: ${lastCustomerID}`)

        if (lastCustomerID && lastUserName.length > 0) {
            storageService.session.set(getUserStateKey(), userState);

            // set in local storage as well to support login automatically
            storageService.local.set(getUserStateKey(), userState);
        }
    //}
}

export function UserContextProvider({ children }: UserContextProviderProps) {

    const currentUserState = storageService.session.get<UserState>(getUserStateKey(), defaultUserState);


    const [userState, dispatchUserState] = useReducer(reducer, currentUserState);

    // Whenever state changes store it in storage
    useEffect(() => {

        storeState(userState);

        document.title = userState.customer?.displayText ?? 'eLynx';

    }, [userState]);

    return (
        <UserContext.Provider value={[userState, dispatchUserState]}>
            {children}
        </UserContext.Provider>
    );
}

export function useUserContext() {
    const context = useContext<[UserState, (action: UserStateAction) => void] | undefined>(UserContext);
    if (!context) {
        throw new Error("useUserContext must be used within a UserContextProvider");
    }
    return context
}
